import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, defer, filter, map, of, switchMap, take, tap } from 'rxjs';
import { environment } from 'src/environment/environment';
import { Permission } from 'src/app/modules/permissions/models/permissions.model';
import {
  PermissionAction,
  PermissionMode,
  PermissionResource,
  buildPermissionCode,
} from '../constants/permissions.constants';
import { GetUserActions } from '../store/actions';
import {
  selectCurrentUserPermissions,
  selectCurrentUserPermissionsLoaded,
  selectIsGettingCurrentUserPermissions,
} from '../store/selectors';
import { AppState } from '../store/state';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  constructor(private readonly store: Store<AppState>) {}

  get permissionsEnabled(): boolean {
    return environment.permissionsEnabled;
  }

  getCurrentPermissions$(): Observable<Permission[]> {
    if (!this.permissionsEnabled) {
      return of([]);
    }

    return defer(() =>
      combineLatest([
        this.store.select(selectCurrentUserPermissionsLoaded),
        this.store.select(selectIsGettingCurrentUserPermissions),
      ]).pipe(
        take(1),
        tap(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.store.dispatch(GetUserActions.getCurrentUserPermissions());
          }
        }),
        switchMap(() =>
          combineLatest([
            this.store.select(selectCurrentUserPermissions),
            this.store.select(selectCurrentUserPermissionsLoaded),
          ]).pipe(
            filter(([, loaded]) => loaded),
            map(([permissions]) => permissions),
          ),
        ),
      ),
    );
  }

  hasPermission$(permission: string): Observable<boolean> {
    if (!this.permissionsEnabled) {
      return of(true);
    }

    return this.getCurrentPermissions$().pipe(
      map((permissions) => this.hasPermission(permissions, permission)),
    );
  }

  hasAnyPermission$(permissionsToCheck: string[]): Observable<boolean> {
    if (!this.permissionsEnabled || !permissionsToCheck.length) {
      return of(true);
    }

    return this.getCurrentPermissions$().pipe(
      map((permissions) => this.hasAnyPermission(permissions, permissionsToCheck)),
    );
  }

  canRead$(resource: PermissionResource): Observable<boolean> {
    return this.hasAnyPermission$([
      buildPermissionCode(resource, PermissionAction.Read),
      buildPermissionCode(resource, PermissionAction.Write),
    ]);
  }

  canWrite$(resource: PermissionResource): Observable<boolean> {
    return this.hasPermission$(buildPermissionCode(resource, PermissionAction.Write));
  }

  canDelete$(resource: PermissionResource): Observable<boolean> {
    if (!this.permissionsEnabled) {
      return of(true);
    }

    return this.getCurrentPermissions$().pipe(
      map((permissions) => this.canDeleteResource(resource, permissions)),
    );
  }

  getMode$(resource: PermissionResource): Observable<PermissionMode> {
    if (!this.permissionsEnabled) {
      return of(PermissionMode.ReadWrite);
    }

    return this.getCurrentPermissions$().pipe(
      map((permissions) => this.getMode(resource, permissions)),
    );
  }

  hasPermission(permissions: Permission[], permission: string): boolean {
    if (!this.permissionsEnabled) {
      return true;
    }

    const expectedPermission = normalizePermissionValue(permission);

    return permissions.some((currentPermission) =>
      getPermissionValues(currentPermission).some(
        (currentValue) => normalizePermissionValue(currentValue) === expectedPermission,
      ),
    );
  }

  hasAnyPermission(permissions: Permission[], permissionsToCheck: string[]): boolean {
    if (!this.permissionsEnabled || !permissionsToCheck.length) {
      return true;
    }

    return permissionsToCheck.some((permission) => this.hasPermission(permissions, permission));
  }

  canReadResource(resource: PermissionResource, permissions: Permission[]): boolean {
    return this.hasAnyPermission(permissions, [
      buildPermissionCode(resource, PermissionAction.Read),
      buildPermissionCode(resource, PermissionAction.Write),
    ]);
  }

  canWriteResource(resource: PermissionResource, permissions: Permission[]): boolean {
    return this.hasPermission(permissions, buildPermissionCode(resource, PermissionAction.Write));
  }

  canDeleteResource(resource: PermissionResource, permissions: Permission[]): boolean {
    if (!this.permissionsEnabled) {
      return true;
    }

    if (!this.canWriteResource(resource, permissions)) {
      return false;
    }

    if (resource === PermissionResource.Users) {
      return this.hasPermission(
        permissions,
        buildPermissionCode(resource, PermissionAction.Delete),
      );
    }

    return true;
  }

  getMode(resource: PermissionResource, permissions: Permission[]): PermissionMode {
    if (!this.permissionsEnabled || this.canWriteResource(resource, permissions)) {
      return PermissionMode.ReadWrite;
    }

    if (this.canReadResource(resource, permissions)) {
      return PermissionMode.ReadOnly;
    }

    return PermissionMode.Hidden;
  }
}

function getPermissionValues(permission: Permission): string[] {
  const values = [
    permission.name,
    permission.code,
    permission.value,
    permission.key,
    permission.permissionName,
    permission.displayName,
  ];

  if (permission.resource && permission.action) {
    values.push(`${permission.resource}.${permission.action}`);
    values.push(`${permission.resource}:${permission.action}`);
    values.push(`${permission.resource} - ${permission.action}`);
  }

  return values.filter((value): value is string => typeof value === 'string' && !!value.trim());
}

function normalizePermissionValue(permission: string): string {
  return permission.trim().toLowerCase();
}
